/**
 * Usuários da equipe (migration 0058): tipos, validação de borda e mensagens legíveis.
 *
 * As regras de acesso (módulos, níveis, predefinições, senha) vêm de `packages/config/src/access`
 * — a MESMA definição que o servidor usa no staff gate, então a tela nunca promete um nível que o
 * servidor não aplica.
 */
import type { AccessLevel, PermissionMap, StaffRoleKey } from "../../../../packages/config/src/access.js";

export type { AccessLevel, PermissionMap, StaffRoleKey };

export type StaffUserStatus = "active" | "blocked";

export interface StaffUser {
  id: string;
  fullName: string;
  email: string;
  role: StaffRoleKey;
  status: StaffUserStatus;
  /** O que foi gravado (vazio = operador com acesso total, regra anterior à 0058). */
  permissions: PermissionMap;
  /** Nível efetivo de cada módulo (admin = tudo `edit`). */
  access: PermissionMap;
  lastSignInAt: string | null;
  createdAt: string | null;
  blockedAt: string | null;
}

export interface StaffUserEvent {
  id: string;
  actor: string;
  action: "created" | "updated" | "blocked" | "unblocked" | "password_changed" | "deleted";
  at: string;
}

export interface NewStaffUserInput {
  fullName: string;
  email: string;
  password: string;
  role: StaffRoleKey;
  permissions: PermissionMap;
}

export interface StaffUserPatch {
  fullName?: string;
  email?: string;
  /** Vazio/ausente = não troca a senha. */
  password?: string;
  role?: StaffRoleKey;
  permissions?: PermissionMap;
  status?: StaffUserStatus;
}

function fail(what: string): never {
  throw new Error(`invalid_staff_user:${what}`);
}

export function assertStaffUser(value: unknown): StaffUser {
  if (!value || typeof value !== "object") return fail("not_an_object");
  const row = value as Record<string, unknown>;
  if (typeof row.id !== "string") return fail("id");
  if (row.role !== "admin" && row.role !== "operator") return fail("role");
  if (row.status !== "active" && row.status !== "blocked") return fail("status");
  if (!row.access || typeof row.access !== "object") return fail("access");
  return {
    id: row.id,
    fullName: typeof row.fullName === "string" ? row.fullName : "",
    email: typeof row.email === "string" ? row.email : "",
    role: row.role,
    status: row.status,
    permissions: (row.permissions && typeof row.permissions === "object" ? row.permissions : {}) as PermissionMap,
    access: row.access as PermissionMap,
    lastSignInAt: typeof row.lastSignInAt === "string" ? row.lastSignInAt : null,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : null,
    blockedAt: typeof row.blockedAt === "string" ? row.blockedAt : null
  };
}

export function assertStaffUserEvent(value: unknown): StaffUserEvent {
  if (!value || typeof value !== "object") return fail("event");
  const row = value as Record<string, unknown>;
  if (typeof row.id !== "string" || typeof row.action !== "string" || typeof row.at !== "string") return fail("event_fields");
  return { id: row.id, actor: typeof row.actor === "string" ? row.actor : "", action: row.action as StaffUserEvent["action"], at: row.at };
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_email: "E-mail inválido.",
  full_name_required: "Informe o nome da pessoa.",
  weak_password: "A senha precisa ter 8+ caracteres, com letras e números.",
  email_already_registered: "Já existe um usuário com este e-mail.",
  cannot_modify_self: "Você não pode excluir, bloquear ou rebaixar a própria conta.",
  last_admin: "Este é o último administrador ativo; o sistema precisa de pelo menos um.",
  admin_role_required: "Só administradores gerenciam usuários.",
  user_not_found: "Usuário não encontrado (talvez já tenha sido excluído).",
  user_blocked: "Esta conta está bloqueada.",
  invalid_role: "Papel inválido.",
  nothing_to_update: "Nada foi alterado."
};

/** Traduz o `reason:path:status` do cliente para uma frase que o operador entende. */
export function userErrorMessage(error: Error): string {
  const reason = error.message.split(":")[0] ?? "";
  if (reason.startsWith("unknown_module") || reason.startsWith("invalid_level")) return "Permissão inválida.";
  return ERROR_MESSAGES[reason] ?? "Não foi possível concluir a operação. Tente novamente.";
}

export const ROLE_LABELS: Record<StaffRoleKey, string> = { admin: "Administrador", operator: "Operador" };

export const ACTION_LABELS: Record<StaffUserEvent["action"], string> = {
  created: "Usuário criado",
  updated: "Dados ou permissões alterados",
  blocked: "Bloqueado",
  unblocked: "Desbloqueado",
  password_changed: "Senha redefinida",
  deleted: "Excluído"
};

/** Gera uma senha forte (letras e números) para o administrador entregar à pessoa. */
function secureRandom(max: number): number {
  const buffer = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buffer);
  return buffer[0] % max;
}

export function generatePassword(random: (max: number) => number = secureRandom): string {
  const letters = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const pick = (set: string) => set[random(set.length)] ?? set[0];
  const chars = [pick(letters), pick(digits), pick(letters), pick(digits)];
  while (chars.length < 12) chars.push(pick(letters + digits));
  // Embaralha para letra/número não ficarem sempre nas mesmas posições.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = random(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
